import React, { useRef } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Container, i18n } from '../../../../../symphony';
import { useActionOnVisible } from '../../../../../shared/public/SlHooks/useActionOnVisible';
import { ISkillItem, SkillItem } from './SkillItem/SkillItem';
import { ITextData, TextRenderer } from '../TextRenderer/TextRenderer';
import { CtaToWelcome, ICtaToWelcome } from '../CtaToWelcome/CtaToWelcome';
import { StaticPagesContext } from '../../../../private/contexts';
import { SlStaticPagesActionTypes, StaticPagesActionsService } from '../../../../private/services/static-pages/sl-static-pages-actions.service';

import s from './Skills.scss';

const textData: ITextData[] = [
  {
    segments: [i18n.t('ai-home.skills-what-will'), i18n.t('ai-home.skills-you'), i18n.t('ai-home.skills-use-ai-for')],
    italicKeys: [1],
    size: 'medium',
  },
  {
    segments: [
      i18n.t('ai-home.skills-click-to-learn'),
    ],
    onlyDesktop: true,
  },
  {
    segments: [
      i18n.t('ai-home.skills-tap-to-learn'),
    ],
    onlyMobile: true,
  },
];

const skillsData: ISkillItem[][] = [
  [
    {
      skill: 'Marketing',
      color: 'brilliant-blue',
      text: i18n.t('ai-home.skill-marketing-desc'),
    },
    {
      skill: 'Design',
      color: 'professional-purple',
      text: i18n.t('ai-home.skill-design-desc'),
    },
    {
      skill: 'Data Analysis',
      color: 'outrageous-orange',
      text: i18n.t('ai-home.skill-data-analysis-desc'),
    },
  ],
  [
    {
      skill: 'Product Management',
      color: 'youthful-yellow',
      text: i18n.t('ai-home.skill-product-management-desc'),
    },
    {
      skill: 'Sales',
      color: 'magnificent-magenta',
      text: i18n.t('ai-home.skill-sales-desc'),
    },
  ],
  [
    {
      skill: 'Customer Support',
      color: 'growing-green',
      text: i18n.t('ai-home.skill-customer-support-desc'),
    },
    {
      skill: 'Finance',
      color: 'brilliant-blue',
      text: i18n.t('ai-home.skill-finance-desc'),
    },
    {
      skill: 'Copywriting',
      color: 'professional-purple',
      text: i18n.t('ai-home.skill-copywriting-desc'),
    },
  ],
  [
    {
      skill: 'Research',
      color: 'outrageous-orange',
      text: i18n.t('ai-home.skill-research-desc'),
    },
    {
      skill: 'HR',
      color: 'youthful-yellow',
      text: i18n.t('ai-home.skill-hr-desc'),
    },
  ],
];

const motivationText: ITextData = {
  segments: [
    i18n.t('ai-home.skills-motivation-do-best'),
    i18n.t('ai-home.skills-motivation-ai-accelerator'),
    i18n.t('ai-home.skills-motivation-program-desc'),
  ],
  boldKeys: [1],
};

const ctaData: ICtaToWelcome = {
  id: 'Skills',
};

export function Skills({ hideMotivation }: { hideMotivation?: boolean; }) {
  useStyles(s);

  const ref = useRef(null);

  useActionOnVisible(ref, () => {
    const { actions$ } = Container.take(
      StaticPagesContext,
      StaticPagesActionsService,
    );
    actions$.next({
      type: SlStaticPagesActionTypes.TrackAILandingPageImpression,
      requestType: 'parallel',
      data: {
        trackParams: {
          sectionTitle: 'Skills',
        },
      },
    });
  });

  return (
    <div ref={ref}>
      <div className="sl-skills">
        <div className="sl-skills__text-data">
          {textData.map((data, key) => (
            <div key={key}><TextRenderer {...data} /></div>
          ))}
        </div>
        <div className="sl-skills__container">
          {skillsData.map((row, i) => (
            <div className="sl-skills__container__row" key={i}>
              {row.map((data, j) => (
                <SkillItem {...data} key={j} />
              ))}
            </div>
          ))}
        </div>
      </div>

      {!hideMotivation && (
        <div className="sl-skills-motivation">
          <div className="sl-skills-motivation__text">
            <TextRenderer {...motivationText} />
          </div>
          <CtaToWelcome {...ctaData} />
        </div>
      )}
    </div>
  );
}
